'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';

interface DropdownOption {
  value: string | number | null;
  label: string;
  renderLabel?: React.ReactNode;
}

interface PortalDropdownProps {
  options: DropdownOption[];
  value: string | number | null;
  onChange: (value: string | number | null) => void;
  'aria-label': string;
  'data-testid': string;
  listTestId?: string;
  optionTestId?: (value: string | number | null) => string;
}

const PortalDropdown = ({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  'data-testid': testId,
  listTestId,
  optionTestId,
}: PortalDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const toggle = useCallback(() => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setIsOpen((prev) => !prev);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        listRef.current &&
        !listRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className="w-full min-h-10 flex items-center justify-between px-3 py-2 bg-white border border-neutral-200 rounded-full
        text-sm hover:border-neutral-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        <span className="flex items-center gap-2 text-neutral-700">
          {selected?.renderLabel ?? selected?.label}
        </span>
        <Icon
          icon={faChevronDown}
          className={`w-3 h-3 text-neutral-500 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen &&
        position &&
        typeof window !== 'undefined' &&
        createPortal(
          <div
            ref={listRef}
            role="listbox"
            aria-label={ariaLabel}
            className="fixed bg-white rounded-lg shadow-lg border border-neutral-200 py-1 max-h-60 overflow-y-auto z-[99999]"
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            data-testid={listTestId}
          >
            {options.map((option) => (
              <button
                key={String(option.value)}
                type="button"
                role="option"
                aria-selected={value === option.value}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(option.value);
                  setIsOpen(false);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className={`h-10 w-full px-3 py-2 text-left text-sm hover:bg-neutral-100 flex items-center gap-2 ${
                  value === option.value
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-neutral-700'
                }`}
                data-testid={optionTestId?.(option.value)}
              >
                {option.renderLabel ?? option.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default PortalDropdown;
